import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAlert } from './ConfirmModal';
import { isNative } from '../utils/capacitor';
import API from '../utils/api';
import { copyToClipboard } from '../utils/clipboard';

// ── Theme palettes — picked by story themes/genre ─────────────────────────────

const PALETTES = [
  { // Midnight dream (default)
    bg1: '#08041f', bg2: '#1a0a4a', bg3: '#2d1b69', bg4: '#4a1080',
    accent: '#c9a8ff', gold: '#f9c74f', border: 'rgba(201,168,255,0.4)',
    glow: 'rgba(111,66,193,0.6)',
  },
  { // Ocean night
    bg1: '#020d1f', bg2: '#0a2a4a', bg3: '#0d3d6b', bg4: '#1a5f8a',
    accent: '#7dd3fc', gold: '#fbbf24', border: 'rgba(125,211,252,0.4)',
    glow: 'rgba(14,165,233,0.6)',
  },
  { // Enchanted forest
    bg1: '#061208', bg2: '#0b2e14', bg3: '#134e22', bg4: '#1a6b2d',
    accent: '#86efac', gold: '#fde68a', border: 'rgba(134,239,172,0.4)',
    glow: 'rgba(34,197,94,0.5)',
  },
  { // Rosy dusk
    bg1: '#1f0818', bg2: '#3d0f2e', bg3: '#6b1644', bg4: '#961d5c',
    accent: '#f9a8d4', gold: '#fde68a', border: 'rgba(249,168,212,0.4)',
    glow: 'rgba(236,72,153,0.5)',
  },
];

function pickPalette(story) {
  const themes = (story.themes || []).join(' ').toLowerCase();
  const title  = (story.title || '').toLowerCase();
  if (/ocean|sea|water|fish|mermaid|pirate/.test(themes + title)) return PALETTES[1];
  if (/forest|jungle|tree|nature|dragon|adventure/.test(themes + title)) return PALETTES[2];
  if (/princess|fairy|flower|butterfly|pink|love/.test(themes + title)) return PALETTES[3];
  return PALETTES[0];
}

// ── Canvas helpers ─────────────────────────────────────────────────────────────

function drawStars(ctx, W, H, seed) {
  // Small background stars
  for (let i = 0; i < 140; i++) {
    const x = ((seed * 7 + i * 137.508) % W + W) % W;
    const y = ((seed * 13 + i * 97.31)  % H + H) % H;
    const r = 0.5 + (i % 5) * 0.3;
    const a = 0.15 + (i % 6) * 0.08;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${a})`;
    ctx.fill();
  }
  // Bright twinkle stars with cross glow
  for (let i = 0; i < 24; i++) {
    const x = ((seed * 3 + i * 211.7) % W + W) % W;
    const y = ((seed * 17 + i * 163.1) % H + H) % H;
    const sz = 2 + (i % 4);
    const a = 0.35 + (i % 4) * 0.1;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - 1, y - sz, 2, sz * 2);
    ctx.fillRect(x - sz, y - 1, sz * 2, 2);
    // centre dot
    ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
}

function drawGlowCircle(ctx, x, y, r, color) {
  // color is already an rgba(...) string from the palette
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, maxW) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word; }
    else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

async function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload  = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load failed')); };
    img.src = url;
  });
}

// ── Main card renderer ─────────────────────────────────────────────────────────
//
// Layout uses two fixed zones:
//   FOOTER_ZONE  = bottom 80px (square) / 96px (story) — brand + reading time
//   CONTENT_ZONE = everything above FOOTER_ZONE
// textTop advances top-to-bottom inside CONTENT_ZONE.
// Every optional element checks remaining space before drawing.
//
async function drawCard(story, format, API) {
  const W  = 1080;
  const H  = format === 'story' ? 1920 : 1080;
  const iS = format === 'story'; // shorthand
  const p  = pickPalette(story);
  const seed = story.title?.length || 42;

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Zone constants ──
  // SAFE_BOTTOM: nothing (except footer) draws below this line
  const FOOTER_H    = iS ? 96 : 80;
  const SAFE_BOTTOM = H - FOOTER_H;

  // ── 1. Background ──
  const bg = ctx.createLinearGradient(0, 0, W * 0.6, H);
  bg.addColorStop(0, p.bg1); bg.addColorStop(0.35, p.bg2);
  bg.addColorStop(0.7, p.bg3); bg.addColorStop(1, p.bg4);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  drawGlowCircle(ctx, W * 0.55, H * 0.38, W * 0.7, p.glow);
  drawStars(ctx, W, H, seed);

  // ── 2. Illustration ──
  // Heights chosen so the text zone always has ≥450px (square) / ≥820px (story)
  const PAD  = 56;
  const ILL_H = iS ? 780 : 430;   // was 820/550 — reduced to guarantee text room
  const ILL_W = W - PAD * 2;
  const ILL_Y = PAD;
  let hasIll  = false;
  let textTop;

  if (story.illustrationUrl) {
    try {
      const res = await API.get(`/stories/${story._id}/card-image`, { responseType: 'blob' });
      const { img, url } = await loadImageFromBlob(res.data);

      // Glow halo behind image
      ctx.save();
      ctx.shadowColor = p.glow; ctx.shadowBlur = 40;
      roundRectPath(ctx, PAD - 4, ILL_Y - 4, ILL_W + 8, ILL_H + 8, 34);
      ctx.fillStyle = p.glow; ctx.fill();
      ctx.restore();

      // Thin accent border
      ctx.save();
      roundRectPath(ctx, PAD - 2, ILL_Y - 2, ILL_W + 4, ILL_H + 4, 34);
      ctx.strokeStyle = p.border; ctx.lineWidth = 2.5; ctx.stroke();
      ctx.restore();

      // Image — cover crop
      ctx.save();
      roundRectPath(ctx, PAD, ILL_Y, ILL_W, ILL_H, 32);
      ctx.clip();
      const iar = img.width / img.height, bar = ILL_W / ILL_H;
      let dW, dH, dX, dY;
      if (iar > bar) { dH = ILL_H; dW = ILL_H * iar; dX = PAD + (ILL_W - dW) / 2; dY = ILL_Y; }
      else           { dW = ILL_W; dH = ILL_W / iar; dX = PAD; dY = ILL_Y + (ILL_H - dH) / 2; }
      ctx.drawImage(img, dX, dY, dW, dH);

      // Bottom-to-background fade (covers lower ~48% of illustration)
      const fade = ctx.createLinearGradient(0, ILL_Y + ILL_H * 0.52, 0, ILL_Y + ILL_H);
      fade.addColorStop(0, 'rgba(0,0,0,0)');
      fade.addColorStop(1, p.bg2 + 'f8');
      ctx.fillStyle = fade;
      ctx.fillRect(PAD, ILL_Y, ILL_W, ILL_H);

      // Crop-proof brand watermark stamped on the illustration itself, so the
      // brand travels with the image even if someone crops off the footer.
      ctx.shadowBlur = 0;
      ctx.textAlign = 'right';
      ctx.font = `600 ${Math.round(W * 0.026)}px system-ui, -apple-system, Arial`;
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fillText('dreamybedtime.com', PAD + ILL_W - 16, ILL_Y + ILL_H - 14);
      ctx.restore();

      URL.revokeObjectURL(url);
      hasIll  = true;
      textTop = ILL_Y + ILL_H + (iS ? 36 : 24);
    } catch {
      textTop = iS ? 220 : 140;
    }
  } else {
    textTop = iS ? 220 : 140;
  }

  // ── Helper: remaining space in content zone ──
  const rem = () => SAFE_BOTTOM - textTop;

  // ── 3. Moon icon ──
  // Smaller when illustration present (less vertical real-estate needed)
  const moonFS = hasIll ? (iS ? 64 : 46) : (iS ? 96 : 74);
  ctx.font = `${moonFS}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('🌙', W / 2, textTop + moonFS * 0.5);
  textTop += moonFS + (iS ? 18 : 12);

  // ── 4. Title ──
  // Font size scales to leave room; capped at 2 lines (square+ill) or 3 lines (story/no-ill)
  const titleFS = hasIll ? (iS ? 78 : 58) : (iS ? 88 : 66);
  const titleLH = titleFS * 1.2;
  const maxTL   = hasIll && !iS ? 2 : 3;   // 2 lines for square+illustration, 3 otherwise

  ctx.font = `700 ${titleFS}px Georgia,"Times New Roman",serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 16; ctx.shadowOffsetY = 3;
  ctx.fillStyle = '#ffffff';
  const titleLines = wrapText(ctx, story.title || 'Bedtime Story', W - 160);
  const drawnTL = Math.min(titleLines.length, maxTL);
  titleLines.slice(0, drawnTL).forEach((ln, i) => ctx.fillText(ln, W / 2, textTop + i * titleLH));
  ctx.restore();
  textTop += drawnTL * titleLH + (iS ? 28 : 18);

  // ── 5. Divider ──
  if (rem() > 40) {
    const dW = iS ? 280 : 210;
    const dX = (W - dW) / 2;
    const dG = ctx.createLinearGradient(dX, 0, dX + dW, 0);
    dG.addColorStop(0, 'rgba(255,255,255,0)');
    dG.addColorStop(0.35, p.accent);
    dG.addColorStop(0.65, p.gold);
    dG.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = dG;
    ctx.fillRect(dX, textTop, dW, 2);
    // diamond
    ctx.save();
    ctx.translate(W / 2, textTop + 1); ctx.rotate(Math.PI / 4);
    ctx.fillStyle = p.gold; ctx.fillRect(-4, -4, 8, 8);
    ctx.restore();
    textTop += iS ? 22 : 16;
  }

  // ── 6. Child name pill ──
  const childName = story.publishedChildName || story.child?.name || null;
  const childFS   = iS ? 40 : 30;
  const childPillH = childFS + 16;
  if (childName && rem() > childPillH + 20) {
    const label = `✨ A story for ${childName}`;
    ctx.font = `${childFS}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const tw = ctx.measureText(label).width;
    const ph = childPillH, pw = Math.min(tw + 50, W - 80);
    const px = (W - pw) / 2, py = textTop;
    ctx.save();
    roundRectPath(ctx, px, py, pw, ph, ph / 2);
    ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fill();
    ctx.strokeStyle = p.border; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.restore();
    ctx.fillStyle = p.accent;
    ctx.fillText(label, W / 2, py + ph / 2);
    textTop += ph + (iS ? 24 : 14);
  }

  // ── 7. Moral lesson ──
  const mFS  = iS ? 34 : 25;
  const mLH  = mFS * 1.5;
  if (story.moralLesson && rem() > mLH + 20) {
    ctx.font = `italic ${mFS}px Georgia,serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = 'rgba(255,255,255,0.62)';
    const mLines   = wrapText(ctx, `\u201C${story.moralLesson}\u201D`, W - 220);
    // Only draw as many moral lines as actually fit
    const maxML    = Math.min(2, Math.floor(rem() / mLH));
    mLines.slice(0, maxML).forEach((ln, i) => ctx.fillText(ln, W / 2, textTop + i * mLH));
    textTop += maxML * mLH + (iS ? 20 : 12);
  }

  // ── 8. Reading-time badge ── (only if ≥60px still available above footer)
  const rtFS = iS ? 30 : 22;
  const rtPillH = rtFS + 12;
  if (story.readingTime && rem() > rtPillH + 16) {
    const label = `📖 ${story.readingTime} min read`;
    ctx.font = `${rtFS}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const tw = ctx.measureText(label).width;
    const ph = rtPillH, pw = tw + 32;
    const px = (W - pw) / 2, py = textTop;
    ctx.save();
    roundRectPath(ctx, px, py, pw, ph, 7);
    ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fill();
    ctx.restore();
    ctx.fillStyle = 'rgba(255,255,255,0.48)';
    ctx.fillText(label, W / 2, py + ph / 2);
  }

  // ── 9. Brand footer — fixed at bottom, always drawn ──
  const brandFS = iS ? 34 : 25;
  ctx.font = `600 ${brandFS}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(201,168,255,0.5)';
  ctx.fillText('🌙 dreamybedtime.com', W / 2, H - FOOTER_H / 2);

  // ── 10. Outer border glow ──
  ctx.save();
  ctx.strokeStyle = p.border; ctx.lineWidth = 5;
  ctx.shadowColor = p.glow; ctx.shadowBlur = 20;
  ctx.strokeRect(3, 3, W - 6, H - 6);
  ctx.restore();

  return canvas;
}

// ── Web Share API helper ───────────────────────────────────────────────────────

async function getCardBlob(story, format, API) {
  const canvas = await drawCard(story, format, API);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

function canShareFiles() {
  try {
    const testFile = new File([''], 'test.png', { type: 'image/png' });
    return !!navigator.share && navigator.canShare?.({ files: [testFile] });
  } catch { return false; }
}

// ── Mini card preview (CSS only) ──────────────────────────────────────────────

function CardPreview({ story, format }) {
  const p = pickPalette(story);
  const isStory = format === 'story';

  return (
    <div style={{
      aspectRatio: isStory ? '9/16' : '1/1',
      background: `linear-gradient(135deg, ${p.bg1} 0%, ${p.bg2} 35%, ${p.bg3} 70%, ${p.bg4} 100%)`,
      borderRadius: '16px',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: `0 8px 40px ${p.glow}, 0 0 0 1.5px ${p.border}`,
    }}>
      {/* Stars */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {[...Array(28)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: i % 5 === 0 ? '3px' : i % 3 === 0 ? '2px' : '1.5px',
            height: i % 5 === 0 ? '3px' : i % 3 === 0 ? '2px' : '1.5px',
            borderRadius: '50%',
            background: 'white',
            opacity: 0.15 + (i % 6) * 0.07,
            left: `${((i * 137.5) % 100).toFixed(1)}%`,
            top: `${((i * 97.3) % 100).toFixed(1)}%`,
          }} />
        ))}
      </div>

      {/* Glow blob */}
      <div style={{
        position: 'absolute', top: '20%', left: '20%', right: '20%', bottom: '20%',
        background: `radial-gradient(circle, ${p.glow} 0%, transparent 70%)`,
        pointerEvents: 'none', opacity: 0.35,
      }} />

      {/* Outer border glow */}
      <div style={{
        position: 'absolute', inset: '4px',
        border: `1.5px solid ${p.border}`,
        borderRadius: '12px',
        pointerEvents: 'none',
        boxShadow: `inset 0 0 20px ${p.glow}`,
      }} />

      {/* Illustration */}
      {story.illustrationUrl && (
        <div style={{
          position: 'absolute',
          top: '5%', left: '5.5%', right: '5.5%',
          height: isStory ? '43%' : '50%',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1.5px ${p.border}`,
        }}>
          <img src={story.illustrationUrl} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
            background: `linear-gradient(to bottom, transparent, ${p.bg2}fa)`,
          }} />
        </div>
      )}

      {/* Text area */}
      <div style={{
        position: 'absolute',
        top: story.illustrationUrl ? (isStory ? '51%' : '57%') : '15%',
        left: '8%', right: '8%',
        textAlign: 'center',
      }}>
        {/* Moon */}
        <div style={{ fontSize: isStory ? '24px' : '18px', marginBottom: '4px' }}>🌙</div>

        {/* Divider */}
        <div style={{
          height: '1.5px',
          background: `linear-gradient(90deg, transparent, ${p.accent}, ${p.gold}, transparent)`,
          marginBottom: '6px',
        }} />

        {/* Title */}
        <div style={{
          color: '#fff',
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          fontSize: isStory ? '17px' : '13px',
          lineHeight: 1.25,
          marginBottom: '6px',
          textShadow: '0 2px 8px rgba(0,0,0,0.7)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {story.title}
        </div>

        {/* Child name pill */}
        {(story.publishedChildName || story.child?.name) && (
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.1)',
            border: `1px solid ${p.border}`,
            borderRadius: '20px',
            padding: isStory ? '3px 10px' : '2px 8px',
            color: p.accent,
            fontSize: isStory ? '11px' : '9px',
            marginBottom: '5px',
          }}>
            ✨ A story for {story.publishedChildName || story.child?.name}
          </div>
        )}

        {/* Moral */}
        {story.moralLesson && (
          <div style={{
            color: 'rgba(255,255,255,0.6)',
            fontStyle: 'italic',
            fontSize: isStory ? '9.5px' : '8px',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            "{story.moralLesson}"
          </div>
        )}
      </div>

      {/* Brand footer */}
      <div style={{
        position: 'absolute', bottom: '3%', left: 0, right: 0,
        textAlign: 'center', color: 'rgba(201,168,255,0.55)',
        fontSize: isStory ? '10px' : '8.5px', fontWeight: 600,
      }}>
        🌙 dreamybedtime.com
      </div>
    </div>
  );
}

// ── Modal component ────────────────────────────────────────────────────────────

const StoryCardModal = ({ story, onClose }) => {
  const alert = useAlert();
  const [format, setFormat]         = useState('square');
  const [generating, setGenerating] = useState(false);
  const [shareStep, setShareStep]   = useState(null); // 'generating' | 'done'
  const [copied, setCopied]         = useState(false);
  const cardBlobRef = useRef(null);   // cached blob for reuse

  const caption = `🌙 "${story.title}" — a personalised bedtime story made with dreamybedtime.com ✨\n\n@dreamy_bedtime\n#bedtimestory #kidsstories #dreamybedtime`;

  // Pre-generate card blob in background after modal opens
  useEffect(() => {
    cardBlobRef.current = null;
  }, [format]);

  const ensureBlob = useCallback(async () => {
    if (cardBlobRef.current) return cardBlobRef.current;
    const blob = await getCardBlob(story, format, API);
    cardBlobRef.current = blob;
    return blob;
  }, [story, format]);

  // ── Download ──
  // Saves a blob natively on Android via Capacitor Filesystem + Share
  const shareNativeFile = async (blob, filename, title, text) => {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
    await Share.share({ title, text, files: [uri], dialogTitle: title });
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const blob = await ensureBlob();
      const filename = `${(story.title || 'story').replace(/[^a-z0-9 ]/gi, '').replace(/\s+/g, '_')}_card.png`;

      // Android Capacitor: use native Filesystem + Share for reliable save-to-gallery
      if (isNative()) {
        try {
          await shareNativeFile(blob, filename, story.title);
          return;
        } catch (err) {
          if (err?.message?.includes('cancel') || err?.message?.includes('abort')) return;
          // fall through to web approach
        }
      }

      // Web mobile: Web Share API with file
      if (canShareFiles()) {
        const file = new File([blob], filename, { type: 'image/png' });
        try {
          await navigator.share({ files: [file], title: story.title });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }

      // Desktop fallback
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      await alert({ title: 'Download failed', message: 'Could not generate the card. Please try again.', danger: true });
    } finally {
      setGenerating(false);
    }
  };

  // ── Share with image (Web Share API on mobile) ──
  const shareWithImage = async (platform) => {
    setShareStep('generating');
    try {
      const blob = await ensureBlob();
      const shareFilename = 'dreamy-bedtime-story.png';

      // Android Capacitor: native share sheet
      if (isNative()) {
        try {
          await shareNativeFile(blob, shareFilename, story.title, caption);
          setShareStep(null);
          return;
        } catch (err) {
          if (err?.message?.includes('cancel') || err?.message?.includes('abort')) { setShareStep(null); return; }
        }
      }

      if (canShareFiles()) {
        // Web mobile: native share sheet with image file
        const file = new File([blob], shareFilename, { type: 'image/png' });
        const shareData = { files: [file], title: story.title, text: caption };
        await navigator.share(shareData);
        setShareStep(null);
        return;
      }

      // Desktop fallback: download image + open platform
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href     = url;
      a.download = shareFilename;
      a.click();
      URL.revokeObjectURL(url);

      // Small delay so the download starts before the new tab
      await new Promise(r => setTimeout(r, 400));

      if (platform === 'whatsapp') {
        const text = encodeURIComponent(caption);
        window.open(`https://wa.me/?text=${text}`, '_blank');
      } else if (platform === 'instagram') {
        // Instagram has no web URL for direct posting — image is downloaded
        await alert({
          title: '📸 Image saved!',
          message: 'The story card has been downloaded. Open Instagram, tap + and choose the image from your gallery.',
        });
      }
      setShareStep('done');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        await alert({ title: 'Share failed', message: 'Could not share the card. Try downloading it instead.', danger: true });
      }
      setShareStep(null);
    }
  };

  const handleCopyCaption = () => {
    copyToClipboard(caption).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const isMobile     = canShareFiles();
  const isGenerating = generating || shareStep === 'generating';

  return (
    <div
      className="modal show d-block"
      style={{ background: 'rgba(0,0,0,0.72)', zIndex: 1055, backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0" style={{ borderRadius: '24px', overflow: 'hidden', background: 'var(--card-bg)' }}>

          {/* ── Header ── */}
          <div className="modal-header border-0 px-4 pt-4 pb-0">
            <div>
              <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '22px' }}>
                📸 Share Story Card
              </h5>
              <p className="text-muted small mb-0">Create a beautiful image to share with family &amp; friends</p>
            </div>
            <button className="btn-close ms-auto" onClick={onClose} />
          </div>

          <div className="modal-body px-4 pb-4">
            <div className="row g-4 align-items-start">

              {/* ── Left: Preview + format ── */}
              <div className="col-md-5">
                <CardPreview story={story} format={format} />

                {/* Format toggle */}
                <div className="d-flex gap-2 mt-3">
                  {[
                    { value: 'square', icon: '⬜', label: 'Square',  sub: '1:1 · Post' },
                    { value: 'story',  icon: '📱', label: 'Story',   sub: '9:16 · Story' },
                  ].map(f => (
                    <button key={f.value} type="button"
                      onClick={() => setFormat(f.value)}
                      className="flex-fill rounded-3 py-2 border-0"
                      style={{
                        fontSize: '12px', lineHeight: '1.3', cursor: 'pointer',
                        background: format === f.value ? 'linear-gradient(135deg,#6f42c1,#9b59b6)' : 'var(--header-bg)',
                        color: format === f.value ? '#fff' : '#6f42c1',
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        boxShadow: format === f.value ? '0 4px 12px rgba(111,66,193,0.35)' : 'none',
                      }}>
                      <div>{f.icon} {f.label}</div>
                      <div style={{ fontSize: '10px', opacity: 0.8 }}>{f.sub}</div>
                    </button>
                  ))}
                </div>

                {/* Story info tags */}
                <div className="d-flex flex-wrap gap-1 mt-3">
                  {story.readingTime && (
                    <span className="badge rounded-pill px-2 py-1" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                      📖 {story.readingTime} min read
                    </span>
                  )}
                  {(story.themes || []).slice(0, 2).map(t => (
                    <span key={t} className="badge rounded-pill px-2 py-1" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* ── Right: Actions ── */}
              <div className="col-md-7 d-flex flex-column gap-3">

                {/* Share buttons — primary section */}
                <div className="rounded-4 p-4" style={{ background: 'linear-gradient(135deg,#1a0a4a,#2d1b69)', color: 'white' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, opacity: 0.7, marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Share with image
                  </div>
                  <div className="d-flex flex-column gap-2">

                    {/* WhatsApp */}
                    <button
                      onClick={() => shareWithImage('whatsapp')}
                      disabled={isGenerating}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        background: 'rgba(37,211,102,0.15)',
                        border: '1.5px solid rgba(37,211,102,0.5)',
                        borderRadius: '14px', padding: '12px 18px',
                        color: '#fff', cursor: 'pointer', width: '100%',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ width: '36px', height: '36px', background: '#25D366', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="bi bi-whatsapp" style={{ fontSize: '18px', color: '#fff' }}></i>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px' }}>WhatsApp</div>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>
                          {isMobile ? 'Share image directly from your phone' : 'Download card + open WhatsApp'}
                        </div>
                      </div>
                      {shareStep === 'generating'
                        ? <span className="spinner-border spinner-border-sm ms-auto" style={{ color: '#25D366' }}></span>
                        : <i className="bi bi-arrow-right ms-auto" style={{ opacity: 0.5 }}></i>}
                    </button>

                    {/* Instagram */}
                    <button
                      onClick={() => shareWithImage('instagram')}
                      disabled={isGenerating}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        background: 'rgba(220,39,67,0.12)',
                        border: '1.5px solid rgba(220,39,67,0.4)',
                        borderRadius: '14px', padding: '12px 18px',
                        color: '#fff', cursor: 'pointer', width: '100%',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="bi bi-instagram" style={{ fontSize: '18px', color: '#fff' }}></i>
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px' }}>Instagram</div>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>
                          {isMobile ? 'Share to stories or feed from your phone' : 'Download card + post on Instagram'}
                        </div>
                      </div>
                      {shareStep === 'generating'
                        ? <span className="spinner-border spinner-border-sm ms-auto" style={{ color: '#e1306c' }}></span>
                        : <i className="bi bi-arrow-right ms-auto" style={{ opacity: 0.5 }}></i>}
                    </button>
                  </div>
                </div>

                {/* Download button */}
                <button
                  onClick={handleDownload}
                  disabled={isGenerating}
                  className="w-100 rounded-3 py-3 border-0 fw-bold"
                  style={{
                    background: generating ? '#e8d5ff' : 'linear-gradient(135deg,#6f42c1,#9b59b6)',
                    color: '#fff', fontSize: '14px', cursor: 'pointer',
                    boxShadow: generating ? 'none' : '0 4px 16px rgba(111,66,193,0.4)',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  {generating
                    ? <><span className="spinner-border spinner-border-sm"></span>&nbsp;Generating…</>
                    : <><i className="bi bi-download"></i> Download PNG ({format === 'story' ? '9:16' : '1:1'})</>}
                </button>

                {/* Caption */}
                <div className="rounded-3 p-3" style={{ background: 'var(--header-bg)', border: '1.5px solid var(--border-purple)' }}>
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Caption
                    </span>
                    <button
                      onClick={handleCopyCaption}
                      className="btn btn-sm rounded-pill px-3 py-1 border-0"
                      style={{ fontSize: '11px', fontWeight: 600, background: copied ? '#059669' : '#6f42c1', color: '#fff', flexShrink: 0 }}
                    >
                      {copied ? <><i className="bi bi-check me-1"></i>Copied!</> : <><i className="bi bi-clipboard me-1"></i>Copy</>}
                    </button>
                  </div>
                  <p style={{ fontSize: '12.5px', color: 'var(--text-body)', lineHeight: '1.6', margin: 0 }}>
                    🌙 "{story.title}" — a personalised bedtime story made with dreamybedtime.com ✨
                    <br />
                    <span style={{ color: '#9b6fe8' }}>#bedtimestory #kidsstories #dreamybedtime</span>
                  </p>
                </div>

                {/* Tip */}
                <div className="rounded-3 p-3 d-flex gap-3 align-items-start" style={{ background: '#fff8e8', border: '1.5px solid #fde68a' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#92400e', marginBottom: '3px' }}>Tag us!</div>
                    <p style={{ fontSize: '12px', color: '#78350f', margin: 0, lineHeight: 1.5 }}>
                      Tag <strong>@dreamy_bedtime</strong> and we may feature your story on our page. Great for sharing with grandparents and family groups!
                    </p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryCardModal;
